import { useState } from "react";
import { Check, Loader2, Plus, Trash2 } from "lucide-react";
import { BaseSheet } from "@/components/common/BaseSheet";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import type {
  InboxView,
  InboxViewAlertChannel,
  InboxViewAlertUrgency,
  InboxViewInstantAlert,
} from "@/types/inboxView/inboxView";

interface ViewInstantAlertProps {
  view: InboxView;
  isOpen: boolean;
  onClose: () => void;
  isSaving: boolean;
  onSave: (alert: InboxViewInstantAlert | null) => void;
  /** Emails worth offering as one-click recipients (me, teammates, genie report recipients). */
  suggestedEmails?: string[];
}

const URGENCY_OPTIONS: { value: InboxViewAlertUrgency; label: string; hint: string }[] = [
  { value: "normal", label: "Normal", hint: "A plain heads-up" },
  { value: "high", label: "High", hint: "Flagged as high priority" },
  { value: "urgent", label: "Urgent", hint: "Marked URGENT, red in email, siren in Slack" },
];

const CHANNEL_OPTIONS: { value: InboxViewAlertChannel; label: string }[] = [
  { value: "email", label: "Email" },
  { value: "slack", label: "Slack channel" },
  { value: "webhook", label: "Webhook (Zapier, Make, your CRM)" },
];

const EMPTY_ALERT: InboxViewInstantAlert = {
  enabled: true,
  urgency: "normal",
  channel: "email",
  emails: [],
  webhook_url: null,
};

/**
 * Per-view instant alert (HG-5457): when a refresh adds rows to this view, tell
 * the chosen route straight away, at this view's urgency. Opens as a sheet from
 * the view header, like the scheduled email report, and follows its on/off +
 * edit pattern.
 */
export const ViewInstantAlert = ({
  view,
  isOpen,
  onClose,
  isSaving,
  onSave,
  suggestedEmails = [],
}: ViewInstantAlertProps) => {
  const saved = view.instant_alert ?? null;
  const isOn = !!saved?.enabled;

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<InboxViewInstantAlert>(saved ?? EMPTY_ALERT);

  const startEditing = () => {
    setDraft({ ...EMPTY_ALERT, ...(saved ?? {}), enabled: true });
    setEditing(true);
  };

  const handleToggle = (checked: boolean) => {
    if (checked) {
      startEditing();
      return;
    }
    setEditing(false);
    onSave(saved ? { ...saved, enabled: false } : null);
  };

  const handleClose = () => {
    setEditing(false);
    onClose();
  };

  const cleanEmails = draft.emails.map((e) => e.trim()).filter(Boolean);
  const canSave =
    draft.channel === "email" ? cleanEmails.length > 0 : !!draft.webhook_url && draft.webhook_url.trim().length > 0;

  const commit = () => {
    onSave({
      ...draft,
      enabled: true,
      emails: draft.channel === "email" ? cleanEmails : [],
      webhook_url: draft.channel === "email" ? null : draft.webhook_url?.trim() || null,
    });
    setEditing(false);
  };

  const offered = suggestedEmails.filter(
    (email) => !draft.emails.some((e) => e.trim().toLowerCase() === email.trim().toLowerCase()),
  );

  const routeSummary = (alert: InboxViewInstantAlert) => {
    if (alert.channel === "email") return `Email to ${alert.emails.join(", ")}`;
    if (alert.channel === "slack") return "Slack channel";
    return "Webhook";
  };

  return (
    <BaseSheet
      isOpen={isOpen}
      onClose={handleClose}
      title="Instant alert"
      description="Get told as soon as new rows land in this view, with this view's own urgency and route. Views refresh every hour."
      size="lg"
    >
      <div className="flex items-center gap-2">
        <Switch
          checked={isOn || editing}
          onCheckedChange={handleToggle}
          disabled={isSaving}
          aria-label="Instant alert"
        />
        <span className="text-sm font-semibold text-[#222B45]">{isOn ? "On" : editing ? "Not on yet" : "Off"}</span>
      </div>

      {(editing || isOn) && (
        <div className="mt-3 pl-4 border-l-2 border-genie-primary/30 space-y-3">
          {editing ? (
            <>
              <div className="grid gap-3 sm:grid-cols-2 max-w-xl">
                <div>
                  <Label className="text-xs font-medium text-[#64748B] mb-1.5 block">Urgency</Label>
                  <Select
                    value={draft.urgency}
                    onValueChange={(v) => setDraft((d) => ({ ...d, urgency: v as InboxViewAlertUrgency }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {URGENCY_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-[#94A3B8] mt-1">
                    {URGENCY_OPTIONS.find((o) => o.value === draft.urgency)?.hint}
                  </p>
                </div>
                <div>
                  <Label className="text-xs font-medium text-[#64748B] mb-1.5 block">Send to</Label>
                  <Select
                    value={draft.channel}
                    onValueChange={(v) => setDraft((d) => ({ ...d, channel: v as InboxViewAlertChannel }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CHANNEL_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <label className="flex min-h-[44px] sm:min-h-0 items-start gap-2 text-xs text-[#64748B]">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={!!draft.follow_score}
                  onChange={(e) => setDraft((d) => ({ ...d, follow_score: e.target.checked }))}
                />
                <span>
                  <span className="font-medium text-[#222B45]">Raise the urgency for urgent conversations.</span> When a
                  conversation in the alert is rated 70 or more for urgency it goes out as High, and 90 or more as
                  Urgent, even if this view is set lower. It is never lowered.
                </span>
              </label>

              {draft.channel === "email" ? (
                <div>
                  <Label className="text-xs font-medium text-[#64748B] mb-1.5 block">Email recipients</Label>
                  <div className="space-y-2">
                    {draft.emails.map((email, index) => (
                      <div key={index} className="flex gap-2 min-w-0">
                        <input
                          type="email"
                          value={email}
                          onChange={(e) =>
                            setDraft((d) => {
                              const emails = [...d.emails];
                              emails[index] = e.target.value;
                              return { ...d, emails };
                            })
                          }
                          className="flex-1 min-w-0 max-w-sm rounded-md border border-[#E5EAF0] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-genie-primary"
                          placeholder="team@example.com"
                        />
                        <button
                          type="button"
                          aria-label="Remove recipient"
                          onClick={() => setDraft((d) => ({ ...d, emails: d.emails.filter((_, i) => i !== index) }))}
                          className="flex items-center justify-center px-3 py-2 text-red-600 hover:bg-red-50 rounded-md border border-red-200 text-sm shrink-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => setDraft((d) => ({ ...d, emails: [...d.emails, ""] }))}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-genie-primary hover:opacity-80"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Add email
                    </button>
                    {offered.length > 0 && (
                      <div className="flex flex-wrap items-center gap-1.5 pt-1">
                        <span className="text-[10px] uppercase tracking-wider font-semibold text-[#B6C0CC]">Suggested:</span>
                        {offered.map((email) => (
                          <button
                            key={email}
                            type="button"
                            onClick={() => setDraft((d) => ({ ...d, emails: [...d.emails.filter((e) => e.trim()), email] }))}
                            className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border border-[#E5EAF0] bg-white text-[#64748B] hover:border-genie-primary/40 hover:text-genie-primary transition-colors"
                          >
                            <Plus className="h-2.5 w-2.5" />
                            {email}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div>
                  <Label className="text-xs font-medium text-[#64748B] mb-1.5 block">
                    {draft.channel === "slack" ? "Slack webhook URL" : "Webhook URL"}
                  </Label>
                  <input
                    type="url"
                    value={draft.webhook_url ?? ""}
                    onChange={(e) => setDraft((d) => ({ ...d, webhook_url: e.target.value }))}
                    className="w-full max-w-xl rounded-md border border-[#E5EAF0] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-genie-primary"
                    placeholder={
                      draft.channel === "slack" ? "https://hooks.slack.com/services/..." : "https://hooks.zapier.com/..."
                    }
                  />
                  <p className="text-[11px] text-[#94A3B8] mt-1">
                    {draft.channel === "slack"
                      ? "In Slack: Apps, Incoming Webhooks, pick the channel, copy the URL."
                      : "We send a JSON post with the view name, urgency and the new rows."}
                  </p>
                </div>
              )}

              <div className="flex items-center gap-2 pt-1">
                <button
                  onClick={commit}
                  disabled={isSaving || !canSave}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-xs font-semibold disabled:opacity-50"
                  style={{ background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)" }}
                >
                  {isSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                  {isOn ? "Save alert" : "Save & turn on"}
                </button>
                <button
                  onClick={() => setEditing(false)}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold text-[#64748B] hover:text-[#222B45]"
                >
                  Cancel
                </button>
              </div>
            </>
          ) : (
            saved && (
              <div className="text-xs text-[#64748B] space-y-1">
                <div>
                  <span className="font-medium text-[#222B45]">
                    {URGENCY_OPTIONS.find((o) => o.value === saved.urgency)?.label ?? "Normal"}
                  </span>{" "}
                  urgency{saved.follow_score ? ", raised for urgent conversations" : ""} · {routeSummary(saved)}
                  {view.instant_alert_last_sent_at && (
                    <> · last sent {new Date(view.instant_alert_last_sent_at).toLocaleString()}</>
                  )}
                </div>
                <button onClick={startEditing} className="text-genie-primary font-semibold hover:opacity-80">
                  Edit alert
                </button>
              </div>
            )
          )}
        </div>
      )}
    </BaseSheet>
  );
};
